import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Button, Spinner, Alert, Badge, Nav } from 'react-bootstrap';
import { ArrowLeft } from 'lucide-react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  companyService, userService, teamService, departmentService, officeService, ocipIpAllowlistService, scimApiKeyService,
  lockedSignupDomainService, signupDomainService, setAuthToken,
} from '../services/apiService';
import { usePermissions } from '../context/PermissionsContext';
import CompanyAuditLog from '../components/CompanyAuditLog';
import ConfirmDialog from '../components/ConfirmDialog';
import { ErrorState, LoadingState, isNotFound, serverMessage } from '../components/PageState';
import { planKey, planLabel } from '../components/PlanFilter';
import CompanyOverview from '../components/company/CompanyOverview';
import { CompanyUsers, CompanyTeams, CompanyDepartments, CompanyOffices } from '../components/company/CompanyPeople';
import CompanyAnalytics from '../components/company/CompanyAnalytics';
import CompanyBilling from '../components/company/CompanyBilling';
import CompanyConfig from '../components/company/CompanyConfig';
import CompanyBulkImport from '../components/company/CompanyBulkImport';
import { useCompanyList, useLeaveGuard } from '../components/company/companyHooks';
import { frequencyLabel, parsePlainDate } from '../components/company/companyFormat';
import { formatDate } from '../utils/format';

const SCOPES = ['api://7e9ba977-33b0-4e9c-ae75-55f65da317bd/access'];

/** Which section each settings form belongs to, for the leave warning. */
const sectionOfForm = (formKey) => (formKey === 'billing' ? 'Billing' : 'Config');

const CompanyDetailPage = ({ id }) => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canEdit } = usePermissions();
  const [company, setCompany] = useState(null);
  // The last saved company, which each form's save merges its own fields into
  const companyRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [joining, setJoining] = useState(false);
  const [joinStatus, setJoinStatus] = useState(null);
  const [dirtyForms, setDirtyForms] = useState({});
  const [scrollTarget, setScrollTarget] = useState(null);

  // 'deleted-users' is the Users section filtered to deleted users; 'bulk-imports' and 'audit-logs' keep their old keys
  const activeTab = searchParams.get('tab') || 'overview';
  const setActiveTab = useCallback((key) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!key || key === 'overview') {
        next.delete('tab');
      } else {
        next.set('tab', key);
      }
      return next;
    });
  }, [setSearchParams]);

  const getAccessToken = useCallback(async () => {
    const response = await instance.acquireTokenSilent({ scopes: SCOPES, account: accounts[0] });
    return response.accessToken;
  }, [instance, accounts]);

  /** Runs an API call with a fresh token. */
  const api = useCallback(async (request) => {
    setAuthToken(await getAccessToken());
    return request();
  }, [getAccessToken]);

  const listOf = (request) => async () => (await api(request)).data;
  const activeUsers = useCompanyList(useCallback(listOf(() => userService.getUsersByCompany(id)), [api, id]));
  const deletedUsers = useCompanyList(useCallback(listOf(() => userService.getDeletedUsersByCompany(id)), [api, id]));
  const teams = useCompanyList(useCallback(listOf(() => teamService.getTeamsByCompany(id)), [api, id]));
  const departments = useCompanyList(useCallback(listOf(() => departmentService.getDepartmentsByCompany(id)), [api, id]));
  const offices = useCompanyList(useCallback(listOf(() => officeService.getOfficesByCompany(id)), [api, id]));
  // Shared by the header, the overview and Config
  const scimApiKeys = useCompanyList(useCallback(listOf(() => scimApiKeyService.getApiKeys(id)), [api, id]));
  const signupDomains = useCompanyList(useCallback(listOf(() => signupDomainService.list(id)), [api, id]));
  const lockedSignupDomains = useCompanyList(useCallback(listOf(() => lockedSignupDomainService.list(id)), [api, id]));
  const ipAddresses = useCompanyList(useCallback(listOf(() => ocipIpAllowlistService.list(id)), [api, id]));

  const loadCompany = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await api(() => companyService.getCompany(id));
      companyRef.current = response.data;
      setCompany(response.data);
    } catch (err) {
      console.error(err);
      setLoadError(err);
    } finally {
      setLoading(false);
    }
  }, [api, id]);

  useEffect(() => {
    loadCompany();
  }, [loadCompany]);

  /** Saves some of the company's fields, merged into the last saved company, and returns the result. */
  const saveCompany = useCallback(async (fields) => {
    const response = await api(() => companyService.updateCompany(id, { ...companyRef.current, ...fields }));
    companyRef.current = response.data;
    setCompany(response.data);
    return response.data;
  }, [api, id]);

  // Load each section's data when it opens
  const firstTabLoad = useRef(true);
  useEffect(() => {
    const first = firstTabLoad.current;
    firstTabLoad.current = false;
    // The header and overview use these too, so they load with the page and again whenever Config opens
    if (first || activeTab === 'config') {
      scimApiKeys.reload();
      signupDomains.reload();
      lockedSignupDomains.reload();
      ipAddresses.reload();
    }
    if (activeTab === 'users' && !activeUsers.loaded) activeUsers.reload();
    if (activeTab === 'deleted-users' && !deletedUsers.loaded) deletedUsers.reload();
    if (activeTab === 'teams' && !teams.loaded) teams.reload();
    if (activeTab === 'departments' && !departments.loaded) departments.reload();
    // Config's office picker and office names come from the offices list
    if ((activeTab === 'offices' || activeTab === 'config') && !offices.loaded) offices.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Unsaved changes in any settings form guard every way off the page and every switch of section
  const handleDirtyChange = useCallback((formKey, dirty) => {
    setDirtyForms((prev) => (!!prev[formKey] === dirty ? prev : { ...prev, [formKey]: dirty }));
  }, []);
  const dirtySections = [...new Set(Object.keys(dirtyForms).filter((key) => dirtyForms[key]).map(sectionOfForm))];
  const blocker = useLeaveGuard(dirtySections.length > 0);

  /** Opens a section, optionally scrolling to one of its cards. */
  const openSection = (tab, anchor) => {
    setScrollTarget(anchor ? { tab, anchor } : null);
    setActiveTab(tab);
  };

  useEffect(() => {
    if (!scrollTarget || scrollTarget.tab !== activeTab || !company) return undefined;
    const frame = requestAnimationFrame(() => {
      document.getElementById(scrollTarget.anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setScrollTarget(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [scrollTarget, activeTab, company]);

  const sidebarRef = useRef(null);
  // Below lg the sidebar is a strip that scrolls sideways: bring the active section into view
  useEffect(() => {
    const strip = sidebarRef.current;
    const link = strip?.querySelector('.nav-link.active');
    if (!link || strip.scrollWidth <= strip.clientWidth) return;
    const offset = link.getBoundingClientRect().left - strip.getBoundingClientRect().left;
    strip.scrollLeft += offset - (strip.clientWidth - link.offsetWidth) / 2;
  }, [activeTab, company]);

  // Staff move their own account into this company as a company admin: one click, result shown inline
  const handleJoinCompany = async () => {
    try {
      setJoining(true);
      setJoinStatus(null);
      await api(() => userService.joinCompany(id));
      setJoinStatus({ type: 'success', message: 'Joined. Your account is now a company admin in this company.' });
    } catch (err) {
      console.error(err);
      setJoinStatus({ type: 'error', message: `Could not join the company: ${serverMessage(err, err.message || 'the server did not say why')}` });
    } finally {
      setJoining(false);
    }
  };

  const backButton = (
    <Button variant="outline-secondary" onClick={() => navigate('/companies')} className="mb-3">
      <ArrowLeft size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />Back to companies
    </Button>
  );

  if (!company && loadError && !loading) {
    return (
      <Container fluid className="py-4">
        {backButton}
        {isNotFound(loadError) ? (
          <Alert variant="warning">Company not found. It may have been deleted, or the link may be wrong.</Alert>
        ) : (
          <ErrorState
            message={`Could not load this company. ${serverMessage(loadError, '')}`.trim()}
            onRetry={loadCompany}
          />
        )}
      </Container>
    );
  }

  const menuItems = [
    { key: 'overview', title: 'Overview' },
    { key: 'billing', title: 'Billing' },
    { key: 'config', title: 'Config' },
    { key: 'users', title: 'Users' },
    { key: 'teams', title: 'Teams' },
    { key: 'departments', title: 'Departments' },
    { key: 'offices', title: 'Offices' },
    { key: 'analytics', title: 'Analytics' },
    ...(canEdit ? [{ key: 'bulk-imports', title: 'Bulk import' }] : []),
    { key: 'audit-logs', title: 'Audit log' },
  ];
  const navKey = activeTab === 'deleted-users' ? 'users' : activeTab;

  // Plan and billing at a glance: "Annual · Stripe billing · Renews 1 Aug 2026"
  const billingFacts = company ? [
    frequencyLabel(company.paymentFrequency),
    company.usesStripe === true ? 'Stripe billing' : company.usesStripe === false ? 'Manual billing' : null,
    company.renewalDate ? `Renews ${formatDate(parsePlainDate(company.renewalDate))}` : null,
  ].filter(Boolean) : [];

  const sectionProps = { companyId: id, company, canEdit, api, onSaveCompany: saveCompany, onDirtyChange: handleDirtyChange };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col xs={12} md>
          {backButton}
          {company ? (
            <>
              <div className="d-flex flex-wrap align-items-center gap-2 gap-md-3">
                {company.logoUrl && (
                  <img src={company.logoUrl} alt="" style={{ height: 32, maxWidth: 96, objectFit: 'contain' }} />
                )}
                <h1 className="mb-0 text-break">{company.name}</h1>
                <Badge bg={company.enabled ? 'success' : 'secondary'}>
                  {company.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
              <div className="d-flex flex-wrap align-items-center column-gap-2 row-gap-1 mt-2 text-muted small">
                <Badge bg="primary">{planLabel(planKey(company))}</Badge>
                {company.overduePayment && <Badge bg="warning">Overdue</Badge>}
                {billingFacts.length > 0 && <span>{billingFacts.join(' · ')}</span>}
              </div>
            </>
          ) : (
            <h1 className="mb-0 placeholder-glow" aria-hidden="true">
              <span className="placeholder col-8 col-md-4" />
            </h1>
          )}
        </Col>
        {/* On phones the buttons sit in a row under the full-width title */}
        <Col xs={12} md="auto" className="d-flex flex-wrap align-items-end gap-2 mt-3 mt-md-0 pb-1">
          <Button variant="outline-primary" onClick={() => navigate(`/crm/companies/${id}`)}>
            View in CRM
          </Button>
          {canEdit && (
            <Button
              variant="outline-primary"
              onClick={handleJoinCompany}
              disabled={joining}
              title="Move your own account into this company as a company admin"
            >
              {joining && <Spinner as="span" animation="border" size="sm" aria-hidden="true" className="me-2" />}
              {joining ? 'Joining...' : 'Join company'}
            </Button>
          )}
        </Col>
      </Row>

      <div className="text-md-end" role="status" aria-live="polite">
        {joinStatus && (
          <p className={`save-status ${joinStatus.type === 'success' ? 'is-success' : 'is-error'} mb-3`}>{joinStatus.message}</p>
        )}
      </div>

      {scimApiKeys.data.some((key) => key.locked) && (
        <Alert variant="danger" className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <span>The SCIM key for this account has been locked because the user limit was breached.</span>
          <Button variant="outline-secondary" size="sm" onClick={() => openSection('config', 'config-scim')}>
            Unlock it on Config
          </Button>
        </Alert>
      )}

      <Row className="g-4">
        <Col lg={3} xl={2} className="company-sidebar-col">
          <Nav
            ref={sidebarRef}
            className="flex-column company-sidebar"
            activeKey={navKey}
            onSelect={(k) => setActiveTab(k)}
          >
            {menuItems.map((item) => (
              <Nav.Link key={item.key} eventKey={item.key}>
                {item.title}
              </Nav.Link>
            ))}
          </Nav>
        </Col>

        <Col lg={9} xl={10}>
          {!company ? (
            <LoadingState label="Loading company" />
          ) : (
            <>
              {activeTab === 'overview' && (
                <CompanyOverview
                  company={company}
                  signupDomains={signupDomains}
                  lockedSignupDomains={lockedSignupDomains}
                  ipAddresses={ipAddresses}
                  onOpenSection={openSection}
                />
              )}

              {(activeTab === 'users' || activeTab === 'deleted-users') && (
                <CompanyUsers
                  view={activeTab === 'deleted-users' ? 'deleted' : 'active'}
                  onViewChange={(view) => setActiveTab(view === 'deleted' ? 'deleted-users' : 'users')}
                  activeUsers={activeUsers}
                  deletedUsers={deletedUsers}
                />
              )}

              {activeTab === 'teams' && <CompanyTeams teams={teams} />}

              {activeTab === 'departments' && <CompanyDepartments departments={departments} />}

              {activeTab === 'offices' && <CompanyOffices offices={offices} />}

              {activeTab === 'analytics' && (
                <CompanyAnalytics companyId={id} totalUsers={company.totalUsers} api={api} />
              )}

              {activeTab === 'billing' && <CompanyBilling {...sectionProps} />}

              {activeTab === 'config' && (
                <CompanyConfig
                  {...sectionProps}
                  lists={{ signupDomains, lockedSignupDomains, scimApiKeys, ipAddresses, offices }}
                />
              )}

              {activeTab === 'bulk-imports' && canEdit && (
                <CompanyBulkImport
                  companyId={id}
                  api={api}
                  onImported={() => {
                    // The user count in the header and overview changes, and the users list if it was open
                    loadCompany();
                    if (activeUsers.loaded) activeUsers.reload();
                  }}
                />
              )}

              {activeTab === 'audit-logs' && <CompanyAuditLog companyId={id} />}
            </>
          )}
        </Col>
      </Row>

      <ConfirmDialog
        show={blocker.state === 'blocked'}
        title="Leave without saving?"
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        variant="danger"
        onConfirm={() => blocker.proceed?.()}
        onCancel={() => {
          blocker.reset?.();
          setScrollTarget(null);
        }}
      >
        <p className="mb-0">
          Your changes on {dirtySections.join(' and ')} are not saved. Leaving now discards them.
        </p>
      </ConfirmDialog>
    </Container>
  );
};

const CompanyDetail = () => {
  const { id } = useParams();
  // Keyed by id, so opening another company starts from a clean page
  return <CompanyDetailPage key={id} id={id} />;
};

export default CompanyDetail;
